import { Component, OnInit, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl, AbstractControl } from '@angular/forms';
import { isEmpty } from 'lodash-es';
import dataURLtoBlob from 'blueimp-canvas-to-blob';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { Editor, Toolbar } from 'ngx-editor';

import * as dayjs from 'dayjs';
import * as html2canvas from 'html2canvas';

import { of, Observable, Subject } from 'rxjs';
import { takeUntil, tap, finalize } from 'rxjs/operators';

import { FileService } from '@service/file.service';
import { EmailService } from '@service/email.service';
import { QuotationService } from '@service/quotation.service';

import { Email } from '@model/email.model';
import { ErrorBackend } from '@model/error.model';

import { ChannelType } from '@enum/quotation.enum';
import { BackendErrorType } from './send-email.enum';
import { ACCEPTED_FILES_TYPE, EDITOR_CONFIG, CAPTURE_SCREEN_CONFIG, FILE_EXTENSION, FILE_NAME_PREFIX } from './send-email.constant';
import { QuoteData } from '@model/quotation.model';

@Component({
  selector: 'app-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss']
})
export class SendEmailComponent implements OnInit, AfterViewInit, OnDestroy {
  emailForm: UntypedFormGroup;
  sendEmailError: ErrorBackend;
  isLoading: boolean = false;
  editor: Editor;

  validators: Function[];
  errorMessages: any;
  backendErrorMessage: string;
  backendErrorType: BackendErrorType;
  sendEmailFailErrorMessage: string;
  currentQuoteData: QuoteData;

  private onDestroy$: Subject<void> = new Subject();

  public toolbar: Toolbar = EDITOR_CONFIG;
  public acceptFileType: string[] = ACCEPTED_FILES_TYPE;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private fileService: FileService,
    private emailService: EmailService,
    private translate: TranslateService,
    private quotationService: QuotationService,
    private modalRef: BsModalRef
  ) {
    this.validators = [this.isValidEmail];
    this.errorMessages = {
      email: this.translate.instant('invalidEmail')
    };
  }

  ngOnInit(): void {
    this.emailForm = this.getDefaultForm();
    this.editor = new Editor({
      
    });
    this.setCurentQuoteData();

    this.captureScreen();

    this.subscribeInvalidEmail();
    this.subscribeSendEmailFail();

    this.listenToLanguageChange();
  }

  ngAfterViewInit(): void {
    this.replaceMissingEditorIcons();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.editor.destroy();
  }

  captureScreen(): void {
    this.isLoading = true;
    html2canvas.default(document.getElementById('quotation'), CAPTURE_SCREEN_CONFIG)
      .then((res: HTMLCanvasElement) => {
        const blob: Blob = dataURLtoBlob(res.toDataURL());
        const date: string = dayjs().format('DD-MM-YYYY');
        const blobObj: Blob = new Blob([blob], { type: `image/${FILE_EXTENSION}` });

        this.attachFile.setValue([this.fileService.blobToFile(blobObj, `${FILE_NAME_PREFIX}${date}.${FILE_EXTENSION}`)]);
        this.isLoading = false;
      })
      .catch(() => {
        this.isLoading = false;
      });
  }

  addFileAttachment(files: File[]): void {
    Array.from(files).forEach((file: File) => {
      if (this.acceptFileType.indexOf(file.type) > -1) {
        const newValue: File[] = [...this.attachFile.value, file];
        this.attachFile.setValue(newValue);
        this.tempAttachFile.setValue(null);
      }
    });
  }

  removeFileAttachment(file: File): void {
    const remainFile: File = this.attachFile.value.filter((item: File) => item !== file);
    this.attachFile.setValue(remainFile);
  }

  addEmailTag(tag: string): Observable<string> {
    return of(tag.toLowerCase());
  }

  sendEmail(): void {
    this.isLoading = true;
    this.setBackendErrorMessage('');

    this.emailService
      .sendEmail(this.createEmail())
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.modalRef.hide();
        },
        error: (error: any) => {
          this.sendEmailError = error.error;

          if (error.error.code === BackendErrorType.FILE_SIZE_ERROR) {
            this.backendErrorType = BackendErrorType.FILE_SIZE_ERROR;
            this.setFileSizeErrorMessage();
          } else {
            this.backendErrorType = BackendErrorType.SEND_EMAIL_FAIL_ERROR;
            this.setBackendErrorMessage(this.sendEmailFailErrorMessage);
          }
        }
      });
  }

  isValidEmail(control: UntypedFormControl): { [key: string]: boolean } | null {
    return Validators.email(control);
  }

  private createEmail(): Email {
    const to: string = this.stringArrayToString(this.to.value, ',');
    const cc: string = this.stringArrayToString(this.cc.value, ',');

    return {
      to,
      cc,
      attachFile: this.attachFile.value,
      subject: this.subject.value,
      content: this.content.value,
      isSentFromAgent: this.currentQuoteData.channelType === ChannelType.INTERMEDIARY
    };
  }

  private listenToLanguageChange(): void {
    this.translate.onLangChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      if (this.backendErrorType === BackendErrorType.FILE_SIZE_ERROR) {
        this.setFileSizeErrorMessage();
      }
    });
  }

  private subscribeInvalidEmail(): void {
    this.translate
      .stream('invalidEmail')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((text: string) => {
        this.errorMessages.email = text;
      });
  }

  private subscribeSendEmailFail(): void {
    this.translate
      .stream('sendEmailFail')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((text: string) => {
        this.sendEmailFailErrorMessage = text;
        if (this.backendErrorType === BackendErrorType.SEND_EMAIL_FAIL_ERROR) {
          this.setBackendErrorMessage(text);
        }
      });
  }

  private setCurentQuoteData(): void {
    this.quotationService
      .getQuoteData()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: QuoteData) => {
        this.currentQuoteData = data;
      });
  }

  private setFileSizeErrorMessage(): void {
    this.setBackendErrorMessage(this.translate.currentLang === 'en' ? this.sendEmailError.message : this.sendEmailError.messageTh);
  }

  private stringArrayToString(tagArray: any[], middle: string): string {
    if (isEmpty(tagArray)) {
      return '';
    }
    return tagArray.reduce((acc: string, cur: any, index: number) => {
      if (index === 0) {
        return `${cur.value}`;
      }
      return `${acc}${middle}${cur.value}`;
    }, '');
  }

  private setBackendErrorMessage(errorMessage: string): void {
    this.backendErrorMessage = errorMessage;
  }

  private replaceMissingEditorIcons(): void {
    this.replaceWithFontAwesomeIcons('fa-scissors', 'fa-cut');
    this.replaceWithFontAwesomeIcons('fa-files-o', 'fa-copy');
    this.replaceWithFontAwesomeIcons('fa-repeat', 'fa-redo');
    this.replaceWithFontAwesomeIcons('fa-picture-o', 'fa-image');
  }

  private replaceWithFontAwesomeIcons(currentClassName: string, newClassName: string): void {
    const icons: HTMLCollection = document.getElementsByClassName(currentClassName);
    for (let i: number = 0; i < icons.length; i++) {
      icons.item(i).classList.add(newClassName);
      icons.item(i).classList.remove(currentClassName);
    }
  }

  private getDefaultForm(): UntypedFormGroup {
    return this.formBuilder.group({
      to: null,
      cc: null,
      subject: ['', Validators.required],
      attachFile: [[]],
      tempAttachFile: [[]],
      content: ''
    });
  }

  get to(): AbstractControl {
    return this.emailForm.get('to');
  }

  get cc(): AbstractControl {
    return this.emailForm.get('cc');
  }

  get subject(): AbstractControl {
    return this.emailForm.get('subject');
  }

  get attachFile(): AbstractControl {
    return this.emailForm.get('attachFile');
  }

  get tempAttachFile(): AbstractControl {
    return this.emailForm.get('tempAttachFile');
  }

  get content(): AbstractControl {
    return this.emailForm.get('content');
  }
}
